import fetch from 'cross-fetch';
import getApiBaseUrl from './urls';
import { clearLoginCookie } from './loginCookie';

const isAuthError = (json) => {
  try {
    // Using nullish operators to reduce exceptions for debugger
    if (json.errors?.[0].name === 'missing auth token') return true;
  } catch {
    // empty ok
  }
  return false;
};

const handleError = (json) => {
  if (isAuthError(json)) {
    clearLoginCookie();
    window.location.reload();
  } else {
    let message = json?.detail || json?.message;
    if (!message) {
      const pydanticError = json?.errors?.[0];
      message = pydanticError && JSON.stringify(pydanticError);
    }
    if (!message) message = 'Error with api call';
    throw new Error(message);
  }
};

const HEADER = {
  'User-Agent': 'front-end',
};

const JSON_REQ = {
  ...HEADER,
  'Content-Type': 'application/json',
  Accept: 'application/json',
};

/**
 * Sends caught exceptions up to the API so we can fix them
 */
export const logError = (details) =>
  fetch(`${getApiBaseUrl()}/ui-errors`, {
    method: 'PUT',
    headers: JSON_REQ,
    body: JSON.stringify(details),
  })
    // eslint-disable-next-line no-console
    .catch((err) => console.error(`Another error reporting JS error: ${err}`));

const jsonAuth = (jwt, contentType = 'application/json') => {
  const headers = {
    ...HEADER,
    Accept: 'application/json',
  };
  if (jwt) headers.Authorization = `Bearer ${jwt}`;
  if (contentType) headers['Content-Type'] = contentType;
  return headers;
};

const handleResponse = (responseFuture) =>
  responseFuture
    .then((response) => Promise.all([response.ok, response.json()]))
    .then(([responseOk, responseBody]) => {
      if (responseOk) {
        return responseBody;
      }
      return handleError(responseBody);
    });

const simpleGet = (url, jwt, method = 'GET', body = undefined) =>
  handleResponse(
    fetch(url.indexOf('http') !== 0 ? `${getApiBaseUrl()}${url}` : url, {
      method,
      credentials: 'include',
      headers: jsonAuth(jwt),
      body: body ? JSON.stringify(body) : undefined,
    })
  );

export function getAllEquipmentForBuilding(buildingID, jwt, points) {
  const url = `${getApiBaseUrl()}/buildings/${buildingID}/equipment?points=${points}`;
  const request = fetch(url, {
    headers: jsonAuth(jwt),
  }).then((response) => {
    if (response.ok) {
      return response.json();
    }
    return response.json().then((json) => handleError(json));
  });
  return request;
}

export const getAllDevicesForBuilding = (buildingId, jwt) =>
  simpleGet(`${getApiBaseUrl()}/buildings/${buildingId}/devices`, jwt);

export const getAllLocationsForBuilding = (buildingId, jwt) =>
  simpleGet(`${getApiBaseUrl()}/buildings/${buildingId}/location`, jwt);

export const createBuilding = (jwt, name, orgId, address, purpose, timezone, area, areaUnitId) =>
  simpleGet(`/buildings`, jwt, 'POST', {
    name,
    org_id: orgId,
    address,
    purpose,
    timezone,
    area,
    area_unit_id: areaUnitId,
  });

export const deleteBuilding = (buildingId, jwt) =>
  simpleGet(`${getApiBaseUrl()}/buildings/${buildingId}`, jwt, 'DELETE');

export const patchBuilding = (buildingId, patch, jwt) =>
  handleResponse(
    fetch(`${getApiBaseUrl()}/buildings/${buildingId}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
      headers: jsonAuth(jwt),
    })
  );

export const buildingDbo = (buildingId, jwt) =>
  fetch(`${getApiBaseUrl()}/buildings/${buildingId}/dbo`, {
    headers: {
      ...HEADER,
      Authorization: jsonAuth(jwt).Authorization,
      Accept: 'application/x-yaml',
    },
  })
    .then((response) => Promise.all([response.ok, response.blob()]))
    .then(([responseOk, responseBlob]) => {
      if (responseOk) {
        return responseBlob;
      }
      return handleError(responseBlob);
    });

export function getAll(jwt, resourceName) {
  const url = `${getApiBaseUrl()}/${resourceName}`;
  const request = fetch(url, {
    headers: jsonAuth(jwt),
  }).then((response) => {
    if (response.ok) {
      return response.json();
    }
    return response.json().then((json) => handleError(json));
  });
  return request;
}

export const deleteStagingEquipment = (jwt, buildingId, equipIds) =>
  simpleGet(`${getApiBaseUrl()}/staging/${buildingId}/equipment`, jwt, 'DELETE', equipIds);

export function submitLogin(email, pw, remember) {
  const url = `${getApiBaseUrl()}/login`;
  const loginData = {
    login: email.trim(),
    password: pw,
    remember,
  };
  return fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: JSON_REQ,
    body: JSON.stringify(loginData),
  }).then((response) => response.json());
}

export const submitTotp = (password, partialToken) =>
  fetch(`${getApiBaseUrl()}/login/totp`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonAuth(partialToken),
    body: JSON.stringify({
      otp: password,
    }),
  }).then((response) => response.json());

export const startFido2Login = (jwt) => simpleGet(`${getApiBaseUrl()}/login/fido2`, jwt, 'POST');

export const completeFido2Login = (formData, jwt) =>
  handleResponse(
    fetch(`${getApiBaseUrl()}/login/fido2`, {
      method: 'PUT',
      credentials: 'include',
      headers: jsonAuth(jwt),
      body: JSON.stringify(formData),
    })
  );

export function restoreLogin(token) {
  const url = `${getApiBaseUrl()}/whoami`;
  return fetch(url, {
    method: 'GET',
    headers: {
      Accept: 'application/json',
      ...jsonAuth(token),
    },
  }).then((response) => {
    if (response.ok) {
      return response.json();
    }
    return response.json().then(handleError);
  });
}

export function logout(token) {
  return fetch(`${getApiBaseUrl()}/logout`, {
    method: 'POST',
    headers: jsonAuth(token),
  }).then((response) => {
    if (response.ok) {
      return response.json();
    }
    return response.json().then(handleError);
  });
}

export const queryTimeSeriesV2 = (jwt, req, resampleMins = 5) => {
  let url = '/query-v2';
  if (resampleMins != null && resampleMins > 1) {
    url += `?resample_mins=${resampleMins}`;
  }
  return simpleGet(url, jwt, 'POST', req);
};

export const getUsers = (jwt) => simpleGet('/users', jwt);

export const getRoles = (jwt) => simpleGet('/roles', jwt);

export const getRoleTypes = (jwt) => simpleGet('/roles/types', jwt);

export const getRolePermissions = (jwt, roleId) => simpleGet(`/roles/${roleId}/permissions`, jwt);

export const updateRolePermissions = (jwt, roleId, changes) =>
  simpleGet(`/roles/${roleId}/permissions`, jwt, 'POST', changes);

export const getOrganizations = (jwt) => simpleGet('/organizations', jwt);

export const getUnits = (jwt) => simpleGet('/unit', jwt);

export const getScopes = (jwt) => simpleGet('/scopes', jwt);

export const getBuildingIngestStats = (jwt, interval = '24h') =>
  simpleGet(`/ingest-stats?age=${interval}`, jwt);

export const getPointSampleRates = (jwt, buildingId, interval = '24h') =>
  simpleGet(`/ingest-stats/${buildingId}/sampling-rate?interval=${interval}`, jwt);

export const getApiKeys = (jwt) => simpleGet('/api-keys', jwt);

export const createApiKey = (name, scopes, jwt) => {
  const params = new URLSearchParams({ name });
  scopes.forEach((s) => params.append('scopes', s));
  const url = `${getApiBaseUrl()}/api-keys?${params.toString()}`;
  return fetch(url, {
    method: 'POST',
    headers: jsonAuth(jwt),
    body: '',
  })
    .then((response) => Promise.all([response.ok, response.json()]))
    .then(([responseOk, responseBody]) => {
      if (responseOk) {
        return responseBody;
      }
      return handleError(responseBody);
    });
};

// eslint-disable-next-line camelcase
export const editApiKey = (hash, name, scopes, active, api_version, jwt) => {
  const params = new URLSearchParams({ name, active, api_version });
  scopes.forEach((s) => params.append('scopes', s));
  const url = `${getApiBaseUrl()}/api-keys/${hash}?${params.toString()}`;
  return fetch(url, {
    method: 'PATCH',
    headers: jsonAuth(jwt),
  })
    .then((response) => Promise.all([response.ok, response.json()]))
    .then(([responseOk, responseBody]) => {
      if (responseOk) {
        return responseBody;
      }
      return handleError(responseBody);
    });
};

export const deleteApiKey = (hash, jwt) =>
  fetch(`${getApiBaseUrl()}/api-keys/${hash}`, {
    method: 'DELETE',
    headers: jsonAuth(jwt),
  })
    .then((response) => Promise.all([response.ok, response.json()]))
    .then(([responseOk, responseBody]) => {
      if (responseOk) {
        return responseBody;
      }
      return handleError(responseBody);
    });

// Create and edit user are handled the same server-side except for the url you post to
const userCreateEdit = (url) => (data, jwt) =>
  fetch(url, {
    method: 'POST',
    headers: jsonAuth(jwt),
    body: JSON.stringify(data),
  })
    .then((response) => Promise.all([response.ok, response.json()]))
    .then(([ok, body]) => {
      if (ok) return body;
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject({ message: JSON.stringify(body.errors || body.detail) });
    })
    // eslint-disable-next-line prefer-promise-reject-errors
    .catch((e) => Promise.reject(`API error: ${e.message}`));

export const createNewUser = userCreateEdit(`${getApiBaseUrl()}/users`);

export const editUser = (data, id, jwt) =>
  userCreateEdit(`${getApiBaseUrl()}/users/${id}`)(data, jwt);

export const createNewRole = (data, jwt) => {
  const { role_type_id, org_id, name, description } = data;
  return handleResponse(
    fetch(`${getApiBaseUrl()}/roles`, {
      method: 'POST',
      body: JSON.stringify({ role_type_id, org_id, name, description }),
      headers: jsonAuth(jwt),
    })
  );
};

export const editRole = (id, data, jwt) => {
  const { role_type_id, org_id, name, description } = data;
  return simpleGet(`/roles/${id}`, jwt, 'PATCH', { role_type_id, org_id, name, description });
};

const orgHelper = (data, jwt, method, url) => {
  const {
    name,
    short_name,
    name_abbr,
    primary_color,
    secondary_color,
    logo_url,
    mfa_required,
    ingest_alerts,
  } = data;
  return handleResponse(
    fetch(`${getApiBaseUrl()}${url}`, {
      method,
      body: JSON.stringify({
        name,
        short_name,
        name_abbr,
        primary_color,
        secondary_color,
        logo_url,
        mfa_required,
        ingest_alerts,
      }),
      headers: jsonAuth(jwt),
    })
  );
};

export const createNewOrg = (data, jwt) => orgHelper(data, jwt, 'POST', '/organizations');

export const editOrg = (id, data, jwt) => orgHelper(data, jwt, 'PATCH', `/organizations/${id}`);

export const beginPasswordReset = (email, type = 'SELF', variant = 'portal') =>
  handleResponse(
    fetch(`${getApiBaseUrl()}/password-reset/${email.trim()}?type=${type}&variant=${variant}`, {
      method: 'POST',
      headers: JSON_REQ,
    })
  );

export const checkPasswordReset = (email, token) =>
  simpleGet(`${getApiBaseUrl()}/password-reset/${email.trim()}/${token}`, '');

export const updatePasswordViaReset = (email, token, newPassword) =>
  handleResponse(
    fetch(`${getApiBaseUrl()}/password-reset/${email.trim()}/${token}`, {
      method: 'PUT',
      headers: JSON_REQ,
      body: JSON.stringify({ password: newPassword }),
    })
  );

export const fetchTotp = (jwt) => simpleGet('/mfa/totp', jwt);

export const createTotp = (name, jwt) => simpleGet(`/mfa/totp/${name}`, jwt, 'POST');

export const deleteTotp = (name, jwt) => simpleGet(`/mfa/totp/${name}`, jwt, 'DELETE');

export const confirmTotp = (name, password, jwt) =>
  handleResponse(
    fetch(`${getApiBaseUrl()}/mfa/totp/${name}`, {
      method: 'PUT',
      headers: jsonAuth(jwt),
      body: JSON.stringify({ otp: password }),
    })
  );

export const fetchFido2 = (jwt) => simpleGet('/mfa/fido2', jwt);

export const deleteFido2 = (key, jwt) => simpleGet(`/mfa/fido2/${key}`, jwt, 'DELETE');

export const startFido2Setup = (jwt) => simpleGet('/mfa/fido2', jwt, 'POST');

export const completeFido2Setup = (formData, jwt) =>
  handleResponse(
    fetch(`${getApiBaseUrl()}/mfa/fido2`, {
      method: 'PUT',
      credentials: 'include',
      headers: jsonAuth(jwt),
      body: JSON.stringify(formData),
    })
  );

export const deleteUserFido2 = (userId, key, jwt) =>
  simpleGet(`/mfa/fido2/${userId}/${key}`, jwt, 'DELETE');

export const deleteUserTotp = (userId, name, jwt) =>
  simpleGet(`/mfa/totp/${userId}/${name}`, jwt, 'DELETE');

export const fetchUserActions = (jwt) => simpleGet('/account-actions', jwt);

export const fetchActionTypes = (jwt) => simpleGet('/account-actions/types', jwt);

export const queryActions = (query, jwt) => simpleGet('/account-actions/query', jwt, 'POST', query);

export const exportActions = (query, jwt) =>
  fetch(`${getApiBaseUrl()}/account-actions/query`, {
    method: 'POST',
    headers: {
      ...jsonAuth(jwt),
      accept: 'text/csv',
    },
    body: JSON.stringify(query),
  })
    .then((response) => Promise.all([response.ok, response.blob()]))
    .then(([responseOk, responseBlob]) => {
      if (responseOk) {
        return responseBlob;
      }
      return handleError(responseBlob);
    });

export const fetchHomeDirectory = (jwt) => simpleGet('/rtem/files', jwt);

export const fetchSubmissionStatus = (jwt) => simpleGet('/rtem/files/submissions', jwt);

export const sendRtemFilesCommand = (command, jwt) =>
  handleResponse(
    fetch(`${getApiBaseUrl()}/rtem/files`, {
      method: 'POST',
      headers: jsonAuth(jwt),
      body: JSON.stringify(command),
    })
  );

export const submitDataNotification = (path, jwt) =>
  handleResponse(
    fetch(`${getApiBaseUrl()}/rtem/files/submit`, {
      method: 'POST',
      headers: jsonAuth(jwt),
      body: JSON.stringify({ path }),
    })
  );

export const approveDenyDataSubmission = (path, status, jwt) =>
  handleResponse(
    fetch(`${getApiBaseUrl()}/rtem/files/submit`, {
      method: 'PUT',
      headers: jsonAuth(jwt),
      body: JSON.stringify({ path, status }),
    })
  );

export const uploadPresignedRequest = (url, fields, file) => {
  const formData = new FormData();
  Object.entries(fields).forEach(([key, val]) => formData.append(key, val));
  formData.append('file', file);
  return fetch(url, {
    method: 'POST',
    body: formData,
  })
    .then((response) => Promise.all([response.ok, response.blob()]))
    .then(([ok, responseBody]) => (ok ? responseBody : handleError(responseBody)));
};

export const getMeasurements = (jwt) => simpleGet('/measurements', jwt);

export const getNotificationPrefs = (jwt) => simpleGet('/rtem/subscriptions', jwt);

export const changeNotificationPref = (subReq, jwt) =>
  simpleGet('/rtem/subscriptions', jwt, 'POST', subReq);

export const unsubscribeAllNotifications = (jwt) =>
  simpleGet('/rtem/subscriptions/all', jwt, 'DELETE');

export const resubscribeAllNotifications = (jwt) =>
  simpleGet('/rtem/subscriptions/all', jwt, 'POST');

export const getUnitPreferences = (jwt) => simpleGet('/unit-preferences', jwt);

export const setUnitPreference = (req, jwt) => simpleGet('/unit-preferences', jwt, 'POST', req);

export const deleteUnitPreference = (req, jwt) =>
  simpleGet('/unit-preferences', jwt, 'DELETE', req);

export const getDeployments = (jwt) => simpleGet('/deployment', jwt);

export const patchDeployment = (jwt, site, payload) =>
  simpleGet(`/deployment?site=${encodeURIComponent(site)}`, jwt, 'PATCH', payload);

export const upsertDeployment = (jwt, config) => simpleGet('/deployment', jwt, 'POST', config);

export const updateDeploymentCollector = (jwt, site, collectorId, collector) =>
  simpleGet(
    `/deployment/collector?site=${encodeURIComponent(site)}&collector_id=${encodeURIComponent(collectorId)}`,
    jwt,
    'PATCH',
    collector
  );

export const createDeploymentCollector = (jwt, site, config) =>
  simpleGet(`/deployment/collector?site=${encodeURIComponent(site)}`, jwt, 'POST', config);

export const getDeploymentConfig = (jwt, site) =>
  simpleGet(`/deployment/config?site=${encodeURIComponent(site)}`, jwt);

export const upsertDeploymentConfig = (jwt, site, config) =>
  simpleGet(`/deployment/config?site=${encodeURIComponent(site)}`, jwt, 'POST', config);

export const upsertModbusDeployment = (jwt, site, modbusConfig) =>
  simpleGet(
    `/deployment/device/modbus?site=${encodeURIComponent(site)}`,
    jwt,
    'POST',
    modbusConfig
  );

export const deleteModbusDeployment = (jwt, site, ip, port, unit) =>
  simpleGet(
    `/deployment/device/modbus?site=${encodeURIComponent(site)}&ip=${encodeURIComponent(ip)}&port=${port}&unit=${unit}`,
    jwt,
    'DELETE'
  );

export const getDiscovery = (jwt, site) =>
  simpleGet(`/deployment/bacnet/discover?site=${encodeURIComponent(site)}`, jwt);

export const postDiscovery = (jwt, site, buildingId, port, min, max) =>
  simpleGet(`/deployment/bacnet/discover?site=${encodeURIComponent(site)}`, jwt, 'POST', {
    port,
    building_id: buildingId,
    min_device_id: min,
    max_device_id: max,
  });

export const associateBuildings = (jwt, site, buildings) =>
  simpleGet('/deployment/buildings', jwt, 'POST', { site, buildings });

export const getSiteHeartbeats = (jwt, site, age) =>
  simpleGet(`/deployment/heartbeat?site=${encodeURIComponent(site)}&age=${age}`, jwt);

export const getDeploymentModbus = (jwt, site) =>
  simpleGet(`/deployment/device/modbus?site=${encodeURIComponent(site)}`, jwt);

export const getModbusTemplates = (jwt) => simpleGet(`/modbus/templates`, jwt);

export const getBuildingStagingDevices = (jwt, buildingId) =>
  simpleGet(`/staging/${buildingId}/devices`, jwt);

export const getBuildingStagingPoints = (jwt, buildingId) =>
  simpleGet(`/staging/${buildingId}/points`, jwt);

export const getBuildingStagingEquipment = (jwt, buildingId) =>
  simpleGet(`/staging/${buildingId}/equipment`, jwt);

export const getBuildingStagingPointsByTopics = (jwt, buildingId, topics) =>
  simpleGet(`/staging/${buildingId}/points?topics=${topics.join(',')}`, jwt);

export const getEquipmentStaging = (jwt, buildingId, equipId) =>
  simpleGet(
    `/staging/${buildingId}/equipment/${encodeURIComponent(encodeURIComponent(equipId))}`,
    jwt
  );

export const patchStagingPointUpdate = (jwt, buildingId, data) =>
  simpleGet(`/staging/${buildingId}/points`, jwt, 'PATCH', data);

export const postNewStagingEquip = (jwt, buildingId, name) =>
  simpleGet(`/staging/${buildingId}/equipment?equip_name=${encodeURIComponent(name)}`, jwt, 'POST');

export const patchStagingEquipUpdate = (jwt, buildingId, data) =>
  simpleGet(`/staging/${buildingId}/equipment`, jwt, 'PATCH', data);

export const publishStagingEquipment = (jwt, buildingId, equipIds) =>
  simpleGet(`/staging/${buildingId}/apply`, jwt, 'POST', {
    equip_ids: equipIds,
  });

export const unpublish = (jwt, buildingId, equipmentIds, pointIds, pointEquipmentRelationships) =>
  simpleGet(`/staging/${buildingId}/apply`, jwt, 'DELETE', {
    equipment_ids: equipmentIds,
    point_ids: pointIds,
    point_equipment_relationships: pointEquipmentRelationships,
  });

export const validateStagingPoints = (jwt, buildingId, topics = []) => {
  let query = '';
  if (topics.length > 0) query = `?topics=${topics.join(',')}`;
  return simpleGet(`/staging/${buildingId}/validation/points${query}`, jwt);
};

export const validateStagingEquipment = (jwt, buildingId) =>
  simpleGet(`/staging/${buildingId}/validation/equipment`, jwt);

export const createLocationApi = (jwt, buildingId, name, type) =>
  simpleGet(
    `${getApiBaseUrl()}/buildings/${buildingId}/location/${encodeURIComponent(name)}?type=${type}`,
    jwt,
    'POST'
  );

export const modifyLocationsApi = (jwt, buildingId, payload) =>
  simpleGet(`${getApiBaseUrl()}/buildings/${buildingId}/location`, jwt, 'PATCH', payload);

export const deleteLocationApi = (jwt, buildingId, name) =>
  simpleGet(
    `${getApiBaseUrl()}/buildings/${buildingId}/location/${encodeURIComponent(name)}`,
    jwt,
    'DELETE'
  );

export const createFeedsRelationships = (
  jwt,
  buildingId,
  sourceEquipmentId,
  locationNames,
  equipmentIds
) =>
  simpleGet(
    `${getApiBaseUrl()}/buildings/${buildingId}/link/feeds/${sourceEquipmentId}`,
    jwt,
    'POST',
    { location_names: locationNames, equipment_ids: equipmentIds }
  );

export const deleteFeedsRelationships = (
  jwt,
  buildingId,
  sourceEquipmentId,
  locationNames,
  equipmentIds
) =>
  simpleGet(
    `${getApiBaseUrl()}/buildings/${buildingId}/link/feeds/${sourceEquipmentId}`,
    jwt,
    'DELETE',
    { location_names: locationNames, equipment_ids: equipmentIds }
  );

export const createContainsRelationships = (
  jwt,
  buildingId,
  sourceLocationName,
  locationNames,
  equipmentIds
) =>
  simpleGet(
    `${getApiBaseUrl()}/buildings/${buildingId}/link/contains/${sourceLocationName}`,
    jwt,
    'POST',
    { location_names: locationNames, equipment_ids: equipmentIds }
  );

export const deleteContainsRelationships = (
  jwt,
  buildingId,
  sourceLocationName,
  locationNames,
  equipmentIds
) =>
  simpleGet(
    `${getApiBaseUrl()}/buildings/${buildingId}/link/contains/${sourceLocationName}`,
    jwt,
    'DELETE',
    { location_names: locationNames, equipment_ids: equipmentIds }
  );

export const getApiVersions = (jwt) => simpleGet('/api-versions', jwt);

export const getExports = (jwt) => simpleGet('/timeseries/exports', jwt);

/**
 * Export create and edit both take a TimeseriesQuery in the body, any extra values
 * are sent as query parameters
 */
export const createExport = (jwt, exportData) => {
  const { name, description, sampling_mins, start, end, selector, point_ids, units, aggregations } =
    exportData;
  const params = new URLSearchParams({ name, sampling_mins, desc: description });
  const url = `/timeseries/exports?${params.toString()}`;
  return simpleGet(url, jwt, 'POST', { start, end, selector, point_ids, units, aggregations });
};

export const patchExport = (jwt, exportId, exportData) => {
  const { name, description, sampling_mins, start, end, selector, point_ids, units, aggregations } =
    exportData;
  const params = new URLSearchParams({ name, sampling_mins, desc: description });
  const url = `/timeseries/exports/${exportId}?${params.toString()}`;
  return simpleGet(url, jwt, 'PATCH', { start, end, selector, point_ids, units, aggregations });
};

export const refreshExportLinks = (jwt, exportId) =>
  simpleGet(`/timeseries/exports/${exportId}`, jwt, 'POST');

export const getExportDownloadLink = (token, format = 'csv') =>
  `${getApiBaseUrl()}/timeseries/export/${token}?format=${format}`;

export const selectPoints = (jwt, selector) => simpleGet('/points/select', jwt, 'POST', selector);

export const fetchDataAlerts = (jwt) => simpleGet('/data-alerts', jwt);

export const createDataAlert = (jwt, alert) => simpleGet('/data-alerts', jwt, 'POST', alert);

export const updateDataAlert = (jwt, alertId, alert) =>
  simpleGet(`/data-alerts/${alertId}`, jwt, 'PUT', alert);

export const removeAlert = (jwt, alertId) => simpleGet(`/data-alerts/${alertId}`, jwt, 'DELETE');

export const createDataAlertAction = (jwt, alertId, action) =>
  simpleGet(`/data-alerts/${alertId}/actions`, jwt, 'POST', action);

export const updateDataAlertAction = (jwt, alertId, actionId, action) =>
  simpleGet(`/data-alerts/${alertId}/actions/${actionId}`, jwt, 'PUT', action);

export const removeDataAlertAction = (jwt, alertId, actionId) =>
  simpleGet(`/data-alerts/${alertId}/actions/${actionId}`, jwt, 'DELETE');

export const updateLabel = (jwt, label) => simpleGet(`/labels/${label.name}`, jwt, 'POST', label);
